import { useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_MAP_LIGHT_PRESET,
  type MapLightPreset,
} from './map3DConfig'

const MAP_LIGHT_PRESET_REFRESH_MS = 5 * 60 * 1000

type MapLightPresetInput = {
  prefersDarkMode: boolean
  hour: number
}

function normalizeHour(hour: number) {
  return ((Math.floor(hour) % 24) + 24) % 24
}

export function getMapLightPreset({
  prefersDarkMode,
  hour,
}: MapLightPresetInput): MapLightPreset {
  if (!prefersDarkMode) return DEFAULT_MAP_LIGHT_PRESET

  const normalizedHour = normalizeHour(hour)
  if (normalizedHour >= 5 && normalizedHour < 7) return 'dawn'
  if (normalizedHour >= 7 && normalizedHour < 19) return 'day'
  if (normalizedHour >= 19 && normalizedHour < 21) return 'dusk'
  return 'night'
}

function getCurrentHour() {
  return new Date().getHours()
}

function getPrefersDarkMode() {
  return Boolean(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  )
}

type UseMapLightPresetOptions = {
  enabled?: boolean
}

export function useMapLightPreset({ enabled = true }: UseMapLightPresetOptions = {}) {
  const [prefersDarkMode, setPrefersDarkMode] = useState(getPrefersDarkMode)
  const [hour, setHour] = useState(getCurrentHour)

  useEffect(() => {
    if (!enabled) return

    const updateHour = () => setHour(getCurrentHour())
    updateHour()

    const intervalId = window.setInterval(updateHour, MAP_LIGHT_PRESET_REFRESH_MS)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateHour()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mediaQuery) return

    setPrefersDarkMode(mediaQuery.matches)
    const handleChange = (event: MediaQueryListEvent) => setPrefersDarkMode(event.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [enabled])

  return useMemo(() => {
    if (!enabled) return DEFAULT_MAP_LIGHT_PRESET
    return getMapLightPreset({ prefersDarkMode, hour })
  }, [enabled, hour, prefersDarkMode])
}
